<script setup>
    import { ref, reactive, onMounted, computed, watch } from 'vue'
    import { useRouter, onBeforeRouteLeave } from 'vue-router'
    import NavigationBar from '../components/NavigationBar.vue'
    import HeaderComponent from '../components/HeaderComponent.vue'
    import UserRegistComponent from '../components/UserRegistComponent.vue'
    import UserConfirmComponent from '../components/UserConfirmComponent.vue'
    import UserEditComponent from '../components/UserEditComponent.vue'
    import CallApi  from '../utils/callApi.js'

    const router = useRouter();
    const userManageInfo = reactive({
        // 利用者一覧
        registrants: [],
        // 表示対象の利用者
        displayedRegistants: [],
        // 検索条件 - 利用者ID/利用者名
        searchUserId: "",
        // 検索条件 - 状態
        searchStatus: "all",
        // ユーザー管理画面遷移管理
        currentView: 'list',   // 'list' | 'create' | 'edit' | 'confirm'
        // ユーザー登録データ
        registFormData:{
            userId: '',
            userName: '',
            previewImg: '',
            img: '',
            userInfos:[
                { name: '', content:'', placeholder1: '例）メールアドレス', placeholder2: '例）example@example.com'}
            ]
        },
        // ユーザー更新データ
        editFormData:{},
        // ページネーション
        currentPage: 1,
        // キャンセル確認ダイアログ表示フラグ
        showCancelDialog:false,
        // 利用者管理画面 初期化実施フラグ
        moveToList: false,
        // ナビゲーションバー遷移用
        pendingRoute: null,
        allowLeave: false
    })

    const pageSize = ref(10); // 1ページに表示する件数
    const isLoading = ref(false); // ローディング画面表示フラグ
    const loadingMessage = ref('Loading...');
    const confirmMode = ref('create'); // 確認画面表示 切り替え用

    // 日付表示用関数
    const formatViewJST = (dateString) => {
        if (!dateString) return "-"
        return dateString
            .replace('T', ' ')
            .replace('-', '/')
            .replace('-', '/')
            .replace(/\.\d+/, "")
            .replace(/\+.+$/, "")
    }

    // ユーザー情報を表示用に変換
    const parseUserInfo = (userInfoData) => {
        if (!userInfoData) return []
        if (Array.isArray(userInfoData)) {
            return userInfoData.map(item => ({
                name: item.name || '',
                content: item.content || '',
                placeholder1: item.name || '項目名',
                placeholder2: item.content || '入力してください'
            }))
        }
        if (typeof userInfoData === 'string') {
            try {
                const parsed = JSON.parse(userInfoData)
                if (typeof parsed === 'object' && parsed !== null) {
                    return Object.entries(parsed).map(([name, content]) => ({
                        name,
                        content: content ?? '',
                        placeholder1: name,
                        placeholder2: content ?? '入力してください'
                    }))
                }
            } catch (e) {
                return []
            }
        }
        return []
    }
    // 利用者一覧を取得
    const getRegstrants = async () => {
        try {
            // APIにて全利用者を取得
            const data = await CallApi.getAxiosAllUser();
            userManageInfo.registrants = data.user_infos.map(user_info => ({
                id:user_info.id,
                user_id:user_info?.user_id || "",
                user_name:user_info?.user_name  || "",
                is_active: user_info?.is_active || false,
                created_at:formatViewJST(user_info.created_at),
                updated_at:formatViewJST(user_info.updated_at),
                userInfos: parseUserInfo(user_info?.user_info)
            }))
            // 利用者を登録日時でソート
            userManageInfo.registrants = await userManageInfo.registrants.sort((element, nextElement) => {
                return (element['created_at'] > nextElement['created_at']) ? -1 : 1;
            })

            // 表示用の利用者一覧にも同じ内容を設定
            userManageInfo.displayedRegistants = [...userManageInfo.registrants];

        } catch (error) {
            console.error('利用者一覧取得 通信エラー:', error);
            userManageInfo.registrants = {};
        }
    }

    // ユーザー登録情報をクリア
    const resetRegistFormData = () => {
        userManageInfo.registFormData = {
            userId: '',
            userName: '',
            previewImg: '',
            img: '',
            userInfos:[
                { name: '', content:'', placeholder1: '例）メールアドレス', placeholder2: '例）example@example.com'}
            ]
        }
    }
    // ユーザー登録画面の表示
    const showUserRegist = () => {
        userManageInfo.currentView = 'create';
    }
    // 編集画面の表示
    const showUserEdit = (user) => {
        userManageInfo.editFormData = {
            id: user.id,
            userId: user.user_id,
            userName: user.user_name,
            img: null,
            previewImg: '',
            userInfos: user.userInfos || []
        }
        userManageInfo.currentView = 'edit';
    }
    // ユーザー登録確認画面の表示
    const goConfirm = (data) => {
        confirmMode.value = 'create';
        userManageInfo.registFormData = data;
        userManageInfo.currentView = 'confirm';
    }

    // ユーザー情報編集確認画面の表示
    const goConfirmEdit = (data) => {
        confirmMode.value = 'edit';
        userManageInfo.editFormData = data;
        userManageInfo.currentView = 'confirm';
    }
    
    // 顔画像ファイルをbase64 enocodeして変換
    const toBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result)
            reader.onerror = reject
            reader.readAsDataURL(file)
        })
    }

    // ユーザー登録処理
    const registUser = async(userData) =>{
        try {
            // ローディング画面の表示
            isLoading.value = true;
            loadingMessage.value = '登録中...';

            // 顔画像をbase 64 encode化
            const base64Img = await toBase64(userData.img)
            // ユーザー情報をjson型にする
            const userInfoObj = {}
            userData.userInfos.forEach(info => {
                if (info.name) {
                    userInfoObj[info.name] = info.content
                }
            })
            // リクエストデータの設定
            const data = {
                'user_id': userData.userId,
                'user_name': userData.userName,
                'image':base64Img.replace(/^data:image\/[a-zA-Z]+;base64,/, '')
            }
            if (Object.keys(userInfoObj).length > 0) {
                data.user_info = JSON.stringify(userInfoObj)
            }
            // ユーザー登録APIのcall
            const response = await CallApi.postAxiosUserRegist(data);
            // 利用者一覧オブジェクトに追加
            userManageInfo.registrants.unshift({
                id: response.id,
                user_id: data.user_id,
                user_name: data.user_name,
                userInfos: parseUserInfo(JSON.stringify(userInfoObj)),
                created_at: formatViewJST(response.created_at),
                updated_at: formatViewJST(response.created_at),
                is_active: true
            })
            // 表示用の利用者一覧にも同じ内容を設定
            userManageInfo.displayedRegistants = [...userManageInfo.registrants];

            // 登録完了したため入力情報をクリア
            resetRegistFormData();

            alert('登録しました');
            userManageInfo.currentView = 'list';
            userManageInfo.currentPage = 1;

        } catch (error) {
            console.error('利用者登録 通信エラー:', error);
            alert('登録に失敗しました。');
        } finally {
            // ローディング画面の停止
            isLoading.value = false;
        }
    }

    // ユーザー情報の更新
    const updateUser = async(userData) => {
        try {
            // ローディング画面の表示
            isLoading.value = true;
            loadingMessage.value = '更新中...';

            // 画像の更新がある場合、画像をbase 64 encode化
            let imageData = null
            let imageFeature = ""
            if (userData.img instanceof File) {
                imageData = await toBase64(userData.img)
                    .then(base64 => base64.replace(/^data:image\/[a-zA-Z]+;base64,/, ''))
                const data_feature = {
                    'image':imageData
                }
                imageFeature = await CallApi.getAxiosFeature(data_feature);
            }
            // ユーザー情報をjson形式に変換
            const userInfoObj = {}
            userData.userInfos.forEach(info => {
                if (info.name) {
                    userInfoObj[info.name] = info.content
                }
            })
            // リクエストデータの設定
            const data = {
                'id': userData.id,
                'user_id': userData.userId,
                'user_name': userData.userName,
            }
            if (Object.keys(userInfoObj).length > 0) {
                data.user_info = JSON.stringify(userInfoObj)
            }
            if (imageData) {
                data.emb = imageFeature.emb
            }
            // ユーザー更新APIのcall
            const response = await CallApi.patchAxiosUpdUser(data);
            // 更新ユーザーのIDを利用者一覧オブジェクトから検索
            const index = userManageInfo.registrants.findIndex(
                user => user.id === userData.id
            )
            // 利用者一覧オブジェクトも一緒に更新
            if (index !== -1) {
                userManageInfo.registrants[index] = {
                    ...userManageInfo.registrants[index],
                    user_id: userData.userId,
                    user_name: userData.userName,
                    userInfos: parseUserInfo(JSON.stringify(userInfoObj)),
                    updated_at: formatViewJST(response.updated_at)
                }
            }
            // 表示用の利用者一覧にも同じ内容を設定
            userManageInfo.displayedRegistants = [...userManageInfo.registrants];

            alert('更新しました');
            userManageInfo.currentView = 'list';
            userManageInfo.currentPage = 1;
        } catch (error) {
            console.error('利用者情報更新 通信エラー:', error);
            alert('更新に失敗しました。');
        } finally {
            // ローディング画面の停止
            isLoading.value = false;
        }
    }

    // 利用者編集画面に戻る
    const backToEdit = () => {
        userManageInfo.currentView = 'edit';
    }

    // 戻るボタン押下時の動作
    const handleConfirmBack = (mode = confirmMode.value) => {
        if (mode === 'edit') {
            backToEdit();
        } else {
            showUserRegist();
        }
    }

    // 登録 or 編集実行ボタン押下時の動作
    const onComplete = async(userData) => {
        if (confirmMode.value === 'create') {
            await registUser(userData)
        } else {
            await updateUser(userData)
        }
    }
    // 利用者管理画面へ戻る
    const goList = async() => {
        userManageInfo.showCancelDialog = true;
    }

    // キャンセル確認ダイアログボタン 押下時の動作
    const confirmClose = async() => {
        // キャンセル確認ダイアログの非表示
        userManageInfo.showCancelDialog = false;

        // ユーザー登録情報をクリア
        resetRegistFormData();

        // ナビゲーションバーから遷移要求された場合
        if (userManageInfo.pendingRoute) {
            const route = userManageInfo.pendingRoute;
            userManageInfo.pendingRoute = null;
            userManageInfo.allowLeave = true;
            await router.push(route.fullPath);
            return;
        }
        // 利用者管理一覧へ戻る場合
        if (userManageInfo.moveToList) {
            userManageInfo.moveToList = false;
            await loadList();
            return;
        }

        // 利用者管理画面を表示
        userManageInfo.currentView = 'list';
    }
    const cancelClose = async() => {
        userManageInfo.showCancelDialog = false;
        userManageInfo.pendingRoute = null;
    }

    // 利用者の表示
    const viewRegistants = computed(() => {
        
        const registrants = userManageInfo.displayedRegistants || [];
        
        // 全件数がページサイズ以下ならそのまま表示
        if (registrants.length <= pageSize.value) {
        return registrants ;
        }
        
        // 対象ページ範囲の利用者を表示
        const start =
            (userManageInfo.currentPage - 1) *
            pageSize.value;
        const end = start + Number(pageSize.value);

        return registrants.slice(start, end);

    });
    
    // 検索結果の利用者を表示
    const viewSearchUser = () => {
        const keyword = userManageInfo.searchUserId.trim().toLowerCase(); // 利用者ID or 利用者名
        const status = userManageInfo.searchStatus; // 状態

        // 利用者一覧をフィルタ検索
        const filteredUsers = userManageInfo.registrants.filter(user => {
            // 利用者ID または 氏名で検索
            const matchKeyword =
                keyword === "" ||
                user.user_id.toLowerCase().includes(keyword) ||
                user.user_name.toLowerCase().includes(keyword);

            // 状態で検索
            let matchStatus = true;

            if (status === "on") {
                matchStatus = user.is_active === true;
            } else if (status === "off") {
                matchStatus = user.is_active === false;
            }

            return matchKeyword && matchStatus;
        });

        // フィルタ済みの利用者を登録日時でソート
        filteredUsers.sort((element, nextElement) => {
            return (element['created_at'] > nextElement['created_at']) ? -1 : 1;
        });

        userManageInfo.displayedRegistants = filteredUsers;
        userManageInfo.currentPage = 1;
    }

    // 利用者の状態を変更
    const changeUserStatus = async(user) => {
        try {
            // ローディング画面を表示
            isLoading.value = true;
            loadingMessage.value = '更新中...';

            // bodyの設定
            const data = {
                'id': user.id,
                'is_active': user.is_active
            }
            // ユーザー情報更新APIのcall
            await CallApi.patchAxiosUpdUser(data);

            // 表示用の利用者一覧にも同じ内容を設定
            userManageInfo.displayedRegistants = [...userManageInfo.registrants];

            alert(
            `ユーザーID: ${user.user_id} の状態を ${
                user.is_active ? "有効に" : "停止"
            } しました`
             
        );
        } catch (error) {
            console.error('利用者状態変更 通信エラー:', error);
            alert('状態更新に失敗しました。');
            // チェックボックスの状態を元に戻す
            user.is_active = !user.is_active;
            
        } finally {
            // ローディング画面を終了
            isLoading.value = false;
        }
    }

    // 利用者の削除
    const deleteUser = async(user) => {
        try{
            // 削除確認
            const result = confirm(`ユーザーID: ${user.user_id} を削除しますか？`)
            if(!result) {
                return
            }
            // ローディング画面を表示
            isLoading.value = true;
            loadingMessage.value = '削除中...';

            // パラメータ設定
            const params =  {
                "id":user.id
            }
            // ユーザー削除APIのcall
            await CallApi.deleteAxiosUser(params);
            // 画面の一覧から削除
            userManageInfo.registrants =
                userManageInfo.registrants.filter(
                    item => item.id !== user.id
                );
            // 表示用の利用者一覧にも同じ内容を設定
            userManageInfo.displayedRegistants = [...userManageInfo.registrants];
            alert(`ユーザーID: ${user.user_id} を削除しました`)

        } catch (error) {
            console.error('r利用者削除 通信エラー:', error);
            alert('削除に失敗しました。');
        } finally {
            // ローディング画面を非表示
            isLoading.value = false;
        }
        
    }   

    // 登録人数の件数からページネーションの設定
    const totalPages = computed(() => {
        const total = userManageInfo.displayedRegistants?.length || 0;
        return Math.max(
            1, Math.ceil(total / Number(pageSize.value))
        );
    });

    // ページネーション表示判定
    const showPagination = computed(() => {
        return (
            userManageInfo.displayedRegistants?.length >
            Number(pageSize.value)
        );
    });
    // 画面遷移前の入力内容破棄確認
    onBeforeRouteLeave((to, from, next) => {
        //  無限ループ対策
        if (userManageInfo.allowLeave){
            next();
            return;
        }
        // 一覧画面ならそのまま遷移
        if (userManageInfo.currentView === 'list') {
            next();
            return;
        }
        // ダイアログ表示
        userManageInfo.pendingRoute = to;
        userManageInfo.showCancelDialog = true;

        // いったん遷移停止
        next(false);
    });

    onMounted(async () => {
        await loadList();
    })
    const loadUserManagementView = async () => {
        if(userManageInfo.currentView != 'list'){
            // 登録 or 編集画面から呼び出された時、確認画面を表示する
            userManageInfo.moveToList = true;
            userManageInfo.showCancelDialog = true;
            return;
        }
        await loadList();
    }
    const loadList = async () => {
        // ローディング画面の表示
        isLoading.value = true;
        // 利用者一覧の取得
        await getRegstrants();
        userManageInfo.currentView = 'list'
        // ローディング画面の停止
        isLoading.value = false;
    }

    // currentPage が totalPages を超えたら補正する
    watch(totalPages, (newTotalPages) => {
        if (userManageInfo.currentPage > newTotalPages) {
            userManageInfo.currentPage = Math.max(1, newTotalPages);
        }
    })
    
</script>
<template>
    <HeaderComponent></HeaderComponent>
    <NavigationBar @go-user-management="loadUserManagementView"></NavigationBar>
    <main>
        <div v-if="userManageInfo.currentView === 'list'"  class="mainView">
            <div class="titleArea">
                <p class="title">利用者管理</p>
                <div class="titleTextArea">
                    <p class="titleText">登録されている利用者の一覧です。利用者の状態確認や情報の更新、停止が行えます。</p>
                    <button class=nextButton id="userRegistButton" @click="showUserRegist">＋ 新規登録</button>
                </div>
            </div>
            <div class="userManageArea">
                <div class="userFindArea">
                    <input id="inputUserName" type="text" v-model="userManageInfo.searchUserId" placeholder="利用者ID、氏名で検索" class="userFindInput">
                    <p>状態</p>
                    <select id="inputStatusButton" v-model="userManageInfo.searchStatus" >
                        <option value="all">すべて</option>
                        <option value="on">有効</option>
                        <option value="off">停止</option>
                    </select>
                    <button class=exeButton id="userFindButton" @click="viewSearchUser">検索</button>
                </div>
                <div class="userListArea">
                    <div class="userListTableArea">
                        <table class="commonTable">
                            <thead>
                                <tr>
                                    <th>利用者ID</th>
                                    <th>氏名</th>
                                    <th>状態</th>
                                    <th>登録日時</th>
                                    <th>最終更新日時</th>
                                    <th>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                    <tr v-for="user in viewRegistants" :key="user.user_id">
                                        <td id="userId">{{ user.user_id }}</td>
                                        <td id="userName">{{ user.user_name }}</td>
                                        <td id="avtive_status">
                                            <label class="switchContainer">
                                                <input type="checkbox" v-model= user.is_active @change="changeUserStatus(user)">
                                                <div class="switchArea">
                                                    <span class="switchText" v-if=user.is_active>有効</span>
                                                    <span class="switchText" v-else>停止</span>
                                                    <div class="switchSliderArea">
                                                        <div class="switchSliderInner">
                                                            <span class="switchSlider"></span>
                                                        </div>
                                                    </div>
                                                </div >
                                            </label>
                                        </td>
                                        <td id="userCreatedTime">{{ user.created_at }}</td>
                                        <td id="userUpdatedTime">{{ user.updated_at }}</td>
                                        <td id="execButton">
                                            <div id="userButtonArea">
                                                <button class=exeButton id="updateUserButton" @click="showUserEdit(user)">
                                                    <img src="../assets/usermng_edit.png" class="editIcon">
                                                    更新
                                                </button>
                                                <button class=clearButton id="deleteUserButton" @click="deleteUser(user)">
                                                    <img src="../assets/usermng_delete.png" class="deleteIcon">
                                                    削除
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                        </table>
                        <div class="pagination-area" v-if="showPagination">
                            <div class="pagination">
                                <button
                                    class="page-btn"
                                    @click="userManageInfo.currentPage--"
                                    :disabled="userManageInfo.currentPage === 1"
                                >←</button>
                                <button
                                    v-for="page in totalPages"
                                    :key="page"
                                    class="page-btn"
                                    :class="{ active: userManageInfo.currentPage === page }"
                                    @click="userManageInfo.currentPage = page"
                                >{{ page }}</button>
                                <button
                                    class="page-btn"
                                    @click="userManageInfo.currentPage++"
                                    :disabled="userManageInfo.currentPage === totalPages"
                                >→</button>
                            </div>
                            <select
                                v-model="pageSize"
                                class="page-size"
                                @change="userManageInfo.currentPage = 1"
                            >
                                <option value="10">10件 / ページ</option>
                                <option value="20">20件 / ページ</option>
                                <option value="50">50件 / ページ</option>
                            </select>       
                        </div> 
                    </div>
                </div>
            </div>
        </div>
        <UserRegistComponent
            v-if="userManageInfo.currentView === 'create'"
            :formData="userManageInfo.registFormData"
            @goConfirm="goConfirm"
            @close="goList">
        </UserRegistComponent>
        <UserEditComponent
            v-if="userManageInfo.currentView === 'edit'"
            :initialData="userManageInfo.editFormData"
            @goConfirm="goConfirmEdit"
            @close="goList">
        </UserEditComponent>
        <UserConfirmComponent
            v-if="userManageInfo.currentView === 'confirm'"
            :formData="confirmMode === 'create'
                ? userManageInfo.registFormData
                : userManageInfo.editFormData"
            :mode="confirmMode"
            @back="handleConfirmBack"
            @complete="onComplete"
            @close="goList">
        </UserConfirmComponent>
        <div v-if="isLoading" class="loading-overlay">
            <div class="loading-box">
                <p>{{ loadingMessage }}</p>
                <div class="spinner"></div>
            </div>
        </div>
        <div v-if="userManageInfo.showCancelDialog" class="dialog-overlay">
            <div class="dialog-box">
                <p>
                    入力中の内容は破棄されます。<br>キャンセルしてもよろしいですか？
                </p>
                <div class="dialog-button-area">
                    <button class="nextButton" @click="confirmClose">はい</button>
                    <button class="clearButton" @click="cancelClose">いいえ</button>
                </div>
            </div>
        </div>
    </main>
</template>

<style scoped>
    .mainView {
        position: relative;
    }
    .userManageArea {
        padding: 10px 5% 10px 2%;
        width: 100%;
        box-sizing: border-box;
    }
    .pageContent {
        width: 100%;
        min-height: 800px;
        padding: 20px 5% 40px 2%;
        box-sizing: border-box;
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 12px;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    }
    .pageContent > * {
        width: 100%;
    }
    .userFindArea {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 40px;
        margin: 10px 0 30px -10px;
    }
    #inputUserName{
        width: 31%;
        padding: 5px 10px;
        border: 1px solid #969292;
        border-radius: 5px;
    }
    #inputStatusButton{
        width: 15%;
        padding: 5px 10px;
        border: 1px solid #969292;
        border-radius: 5px;
        height: 50px;
    }
    #userButtonArea {
        display: flex;
        gap: 20px;
    }
    #updateUserButton, #deleteUserButton{
        padding: 5px 20px;
    }
    #userId{
        width: 10%;
    }
    #userName{
        width:15%;
    }
    #avtive_status{
        padding-right: 50px;
    }
    #userCreatedTime, #userUpdatedTime{
        width:20%;
    }
    .switchArea{
        width: 110%;
    }
    #execButton{
        min-width: 120px;
    }
    .editIcon, .deleteIcon{
        width:24px;
        height: 24px;
        margin-bottom: 8px;
    }

    .userListTableArea {
        overflow-x: auto;
    }
    .commonTable {
        min-width: 780px;
    }
    .pagination-area {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        align-items: center;
    }
    .dialog-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.4);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
    }
    .dialog-button-area {
        margin-top: 20px;
        display: flex;
        justify-content: center;
        gap: 16px;
    }
    .dialog-box {
        width: 500px;
        background: white;
        border-radius: 8px;
        padding: 32px;
        text-align: center;
        font-size: 18px;
        line-height: 1.8;
    }
    .dialog-box p {
        font-size: 22px;
        font-weight: bold;
        font-weight: 500;
        margin-bottom: 24px;
    }
    .dialog-button-area {
        margin-top: 20px;
        display: flex;
        justify-content: center;
        gap: 16px;
    }
    .dialog-button-area button {
        min-width: 120px;
        font-size: 18px;
        padding: 12px 24px;
    }

    @media (max-width: 1024px) {
        .userFindArea {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
            margin: 10px 0 20px -10px;
        }
        #inputUserName,
        #inputStatusButton,
        .page-size {
            width: 90%;
            max-width: none;
        }
        .userButtonArea {
            flex-wrap: wrap;
            justify-content: flex-start;
        }
    }

    @media (max-width: 768px) {
        .titleTextArea {
            display: flex;
            flex-direction: column;
            align-items: stretch;
        }
        .titleText {
            text-align: left;
            width: 100%;
        }
        .nextButton {
            align-self: center;
            margin-top: 16px;
        }
        .userFindArea {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
        }
        .userFindInput {
            width: 150px;
            max-width: 90%;
        }
        .userFindArea p {
            margin: 0;
            text-align: left;
        }
        .userFindArea select {
            width: 100px;
        }
        .commonTable th,
        .commonTable td {
            padding: 10px 8px;
            font-size: 0.92rem;
        }
        .userInfoTable th,
        .userInfoTable td {
            font-size: 0.92rem;
        }
        #userCreatedTime,
        #userUpdatedTime,
        #userId,
        #userName {
            width: auto;
        }
        .userListArea {
            padding: 0;
        }
        .pagination {
            justify-content: center;
            flex-wrap: wrap;
        }
        .page-btn {
            width: 40px;
            min-width: 40px;
            padding: 6px;
        }
        .page-size {
            width: 100%;
        }
        #userRegistButton {
            max-width: 300px;
        }
    }

    @media (max-width: 540px) {
        .switchArea {
            min-width: 120px;
        }
        #inputUserName,
        #inputStatusButton
        {
            width: 100%;
        }
        .pagination {
            justify-content: space-between;
        }
        .page-btn {
            min-width: 48px;
            padding: 8px 10px;
        }
    }
</style>